// Licences and certifications in the office (BL-027): the kinds the company tracks, a person's
// licences on their profile, the ones expiring, and a visit's gaps, in words. The server's actions:
// licenceKinds.list / save, licences.list / record / remove.

import { dateText } from '@flowology/kernel';

export interface LicenceKind {
  id: string;
  code: string;
  name: string;
  issuer: string | null;
  remindDays: number;
  active: boolean;
  rowVersion: number;
}

export type LicenceStatus = 'valid' | 'expiring' | 'expired' | 'no_expiry';

export interface Licence {
  id: string;
  staffId: string;
  staffName: string;
  kindId: string;
  kindName: string;
  /** staff.edit and the holder only. */
  number?: string | null;
  issuer?: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  notes: string | null;
  status: LicenceStatus;
  daysLeft: number | null;
  rowVersion: number;
}

export interface LicenceGap {
  kindName: string;
  why: 'none' | 'expired' | 'expires_before_visit';
  text: string;
}

type Built = { input: Record<string, unknown> } | { error: string };

// ── where a licence stands ──

/** "Valid until Dec 1, 2027", "Expires in 21 days (Nov 10, 2026)", "Expired Oct 1, 2026", "Doesn't expire". */
export function standingText(
  l: Pick<Licence, 'status' | 'daysLeft' | 'expiresOn'>,
  timeZone: string,
): string {
  if (l.status === 'no_expiry' || !l.expiresOn) return 'Doesn’t expire';
  const on = dateText(l.expiresOn, timeZone);
  if (l.status === 'expired') return `Expired ${on}`;
  if (l.status === 'expiring')
    return l.daysLeft === 0
      ? `Expires today (${on})`
      : `Expires in ${l.daysLeft} day${l.daysLeft === 1 ? '' : 's'} (${on})`;
  return `Valid until ${on}`;
}

/** The chip's tone: red once expired, amber while expiring. */
export const standingTone = (s: LicenceStatus): 'bad' | 'warn' | 'ok' =>
  s === 'expired' ? 'bad' : s === 'expiring' ? 'warn' : 'ok';

/** The line the expiring list shows: "Alex Moreau · Backflow assembly tester". */
export const expiringWho = (l: Pick<Licence, 'staffName' | 'kindName'>): string =>
  `${l.staffName} · ${l.kindName}`;

// ── a person's licence (licences.record) ──

export interface LicenceForm {
  kindId: string;
  number: string;
  issuer: string;
  issuedOn: string;
  expiresOn: string;
  notes: string;
}

export const licenceForm = (l: Licence | null): LicenceForm => ({
  kindId: l?.kindId ?? '',
  number: l?.number ?? '',
  issuer: l?.issuer ?? '',
  issuedOn: l?.issuedOn ?? '',
  expiresOn: l?.expiresOn ?? '',
  notes: l?.notes ?? '',
});

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** licences.record's input: everything for a new one; only what changed (and its version) for one held. */
export function licenceInput(staffId: string, was: Licence | null, f: LicenceForm, id: string): Built {
  if (!f.kindId) return { error: 'Choose which licence it is.' };
  for (const [k, words] of [
    ['issuedOn', 'issue'],
    ['expiresOn', 'expiry'],
  ] as const)
    if (f[k] && !DAY.test(f[k])) return { error: `Pick the ${words} date from the calendar.` };
  if (f.issuedOn && f.expiresOn && f.expiresOn < f.issuedOn)
    return { error: 'It can’t expire before it was issued.' };
  const values: Record<string, string | null> = {
    number: f.number.trim() || null,
    issuer: f.issuer.trim() || null,
    issuedOn: f.issuedOn || null,
    expiresOn: f.expiresOn || null,
    notes: f.notes.trim() || null,
  };
  if (!was) {
    const fresh = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== null));
    return { input: { id, staffId, kindId: f.kindId, ...fresh } };
  }
  const before: Record<string, string | null> = {
    number: was.number ?? null,
    issuer: was.issuer ?? null,
    issuedOn: was.issuedOn,
    expiresOn: was.expiresOn,
    notes: was.notes,
  };
  const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== before[k]));
  if (!Object.keys(changed).length) return { error: 'Nothing has changed yet.' };
  return { input: { id: was.id, rowVersion: was.rowVersion, staffId, kindId: was.kindId, ...changed } };
}

/** What Save says: "Gas fitter (class B) recorded for Sam Rivera, valid until Dec 1, 2027." */
export function licenceSavedText(
  l: Pick<Licence, 'kindName' | 'staffName' | 'status' | 'daysLeft' | 'expiresOn'>,
  timeZone: string,
  isNew: boolean,
): string {
  const s = standingText(l, timeZone);
  return `${l.kindName} ${isNew ? 'recorded' : 'saved'} for ${l.staffName}: ${s.charAt(0).toLowerCase()}${s.slice(1)}.`;
}

// ── a kind the company tracks (licenceKinds.save) ──

export interface KindForm {
  name: string;
  issuer: string;
  remindDays: string;
  active: boolean;
}

export const kindForm = (k: LicenceKind | null): KindForm => ({
  name: k?.name ?? '',
  issuer: k?.issuer ?? '',
  remindDays: String(k?.remindDays ?? 60),
  active: k?.active ?? true,
});

/** A new kind's code from its name: "Gas fitter (class B)" → GAS_FITTER_CLASS_B. */
export const kindCode = (name: string): string =>
  (
    name
      .toUpperCase()
      .normalize('NFKD')
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'L_$1') || 'LICENCE'
  ).slice(0, 30);

export function kindInput(was: LicenceKind | null, f: KindForm, taken: readonly string[]): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give the licence a name.' };
  const days = Number(f.remindDays);
  if (!Number.isInteger(days) || days < 0 || days > 365)
    return { error: 'Remind a whole number of days ahead, 0 to 365.' };
  const values = { name, issuer: f.issuer.trim() || null, remindDays: days };
  if (!was) {
    const base = kindCode(name);
    let code = base;
    for (let n = 2; taken.includes(code); n++) code = `${base.slice(0, 27)}_${n}`;
    return { input: { code, ...values } };
  }
  const changed = Object.fromEntries(
    Object.entries({ ...values, active: f.active }).filter(
      ([k, v]) => v !== (was as unknown as Record<string, unknown>)[k],
    ),
  );
  if (!Object.keys(changed).length) return { error: 'Nothing has changed yet.' };
  return { input: { id: was.id, rowVersion: was.rowVersion, ...changed } };
}

// ── a visit's gaps (jobs.get, appointments.*) ──

/** The job sheet's line under a visit: "Needs a licence: Gas fitter (class B): nobody on this visit holds it." */
export const gapsText = (gaps: readonly LicenceGap[]): string | null =>
  gaps.length
    ? `Needs ${gaps.length === 1 ? 'a licence' : 'licences'}: ${gaps.map((g) => g.text).join(' ')}`
    : null;
